<template>
  <LinkElement
    v-bind="$attrs.onClick ? { onClick: $attrs.onClick } : {}"
    :routerLink="routerLink"
    defaultElement="div"
    :class="[$style.base, { [$style.disabled]: disabled, [$style.loading]: loading, [$style.error]: error }]"
  >
    <div :class="$style.inner">
      <BaseSpinner
        v-if="loading"
        :class="$style.loader"
      />
      <div :class="$style.content">
        <div
          v-if="icon"
          :class="$style.icon"
        >
          <BaseIcon
            :name="icon"
            :size="'s'"
            color="primary"
          />
        </div>
        <div :class="$style.center">
          <BaseText bold>{{ heading }}</BaseText>
          <BaseText
            v-if="description"
            size="s"
            color="secondary"
          >
            {{ description }}
          </BaseText>
          <slot />
        </div>
      </div>
      <BaseIcon
        v-if="chevron"
        name="arrow-right"
      />
    </div>
  </LinkElement>
</template>

<script
  lang="ts"
  setup
>
import type { LocationQueryRaw } from 'vue-router';
import LinkElement from '@/components/_shared/Link.vue';
import type { IconName } from './base-icon/types';

defineProps<{
  chevron?: boolean;
  heading: string;
  description?: string;
  icon?: IconName;
  disabled?: boolean;
  routerLink?: LocationQueryRaw | string;
  loading?: boolean;
  error?: boolean;
}>();

defineOptions({
  inheritAttrs: false,
});
</script>

<style
  lang="scss"
  module
>
.base {
  border-radius: $radius;
  background-color: var(--surface-primary);
  border: 1px solid var(--border);
  padding: $spacing;
  width: 100%;
  cursor: pointer;

  @include hover {
    background-color: var(--highlight);
  }

  &.disabled {
    @include input-disabled;
  }

  &.loading {
    pointer-events: none;
  }

  &.error {
    border-color: var(--error-base);
  }
}

.inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative; // Needed for positioning of the spinner
}

.content {
  display: flex;
  align-items: center;
  gap: $spacing;

  .base.loading & {
    opacity: 0;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--highlight);
}

.center {
  display: flex;
  flex-direction: column;
}

.loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing;
}
</style>
